import React from 'react';
import clsx from 'clsx';
import { Button } from './ui';
import { submitDiscussionPollResponse } from '../lib/cloudFunctions';
import {
  subscribePollCompletion,
  subscribePollQuestions,
  subscribePollResultSummary
} from '../lib/firestore';
import type {
  DiscussionPollCompletion,
  DiscussionPollQuestion,
  DiscussionPollResultSummary
} from '../lib/types';

export function DiscussionPoll(props: { topicId: string; profileId?: string }) {
  const { topicId, profileId } = props;
  const [questions, setQuestions] = React.useState<DiscussionPollQuestion[]>([]);
  const [completion, setCompletion] = React.useState<DiscussionPollCompletion | null>(null);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [results, setResults] = React.useState<Record<string, DiscussionPollResultSummary | null>>({});
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    return subscribePollQuestions(topicId, setQuestions);
  }, [topicId]);

  React.useEffect(() => {
    if (!profileId) {
      setCompletion(null);
      return;
    }
    return subscribePollCompletion(topicId, profileId, setCompletion);
  }, [topicId, profileId]);

  React.useEffect(() => {
    if (!completion?.answers) return;
    setAnswers(completion.answers);
  }, [completion?.answers]);

  React.useEffect(() => {
    if (!completion || !questions.length) {
      setResults({});
      return;
    }

    const unsubs = questions.map((question) =>
      subscribePollResultSummary(topicId, question.id, (summary) => {
        setResults((prev) => ({ ...prev, [question.id]: summary }));
      })
    );
    return () => unsubs.forEach((unsub) => unsub());
  }, [completion, questions, topicId]);

  const answeredAll = questions.length > 0 && questions.every((question) => answers[question.id]);
  const completed = Boolean(completion);

  function setAnswer(questionId: string, optionId: string) {
    if (completed) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  }

  async function submit() {
    if (!answeredAll || !profileId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await submitDiscussionPollResponse({ topicId, answers });
    } catch (err: any) {
      console.error(err);
      setError(err?.message ? String(err.message) : 'Failed to submit poll response');
    } finally {
      setSubmitting(false);
    }
  }

  if (!questions.length) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600">
        Poll loading
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-900">Poll</div>
          {!completed ? (
            <div className="mt-0.5 text-xs text-zinc-600">Answer every question to submit and view the results.</div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,22rem),1fr))] gap-3">
        {questions.map((question, questionIndex) => {
          const selectedOptionId = answers[question.id];
          const result = results[question.id];
          const responseCount = result?.responseCount ?? question.responseCount ?? 0;

          return (
            <div key={question.id} className="rounded-xl border border-zinc-200 bg-white p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm font-semibold text-zinc-900">
                  {questionIndex + 1}. {question.question}
                </div>
                {completed ? (
                  <div className="shrink-0 text-xs text-zinc-600">
                    {responseCount} {responseCount === 1 ? 'response' : 'responses'}
                  </div>
                ) : null}
              </div>

              <div className="mt-3 space-y-2">
                {question.options.map((option) => {
                  const selected = selectedOptionId === option.id;
                  const total = result?.totals?.[option.id] ?? 0;
                  const percent = responseCount > 0 ? Math.round((total / responseCount) * 100) : 0;

                  if (!completed) {
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => setAnswer(question.id, option.id)}
                        className={clsx(
                          'flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm transition',
                          selected
                            ? 'border-accent bg-accent/10 text-accent'
                            : 'border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50'
                        )}
                      >
                        <span>{option.label}</span>
                        {selected ? <span className="text-xs font-semibold">Selected</span> : null}
                      </button>
                    );
                  }

                  return (
                    <div key={option.id} className="rounded-xl border border-zinc-200 bg-zinc-50 p-2">
                      <div className="flex items-center justify-between gap-2 text-sm">
                        <div className="min-w-0">
                          <span className="text-zinc-900">{option.label}</span>
                          {selected ? <span className="ml-2 text-xs font-semibold text-accent">Your vote</span> : null}
                        </div>
                        <div className="shrink-0 text-xs text-zinc-600">
                          {percent}% · {total}
                        </div>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-200">
                        <div className="h-full rounded-full bg-accent" style={{ width: `${percent}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {!completed ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-zinc-600">
            {answeredAll ? 'Ready to submit.' : 'Complete all questions before submitting.'}
          </div>
          <Button onClick={submit} disabled={!answeredAll || submitting || !profileId}>
            {submitting ? 'Submitting...' : 'Submit poll'}
          </Button>
        </div>
      ) : null}

      {error ? (
        <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-2 text-xs text-red-800">{error}</div>
      ) : null}
    </div>
  );
}
